import { Injectable } from 'angular-ts-decorators';

import { DateSource } from '@shared/app/scripts/services/date-source';
import { Moment } from 'moment';

export interface IStartEndStrings {
  // TODO rename to start
  startDate: string;
  // TODO rename to end
  endDate?: string;
}

// TODO remove after the above one is renamed
export interface IStartEndRenamedStrings {
  start: string;
  end?: string;
}

export interface IStartEndDates {
  startDate: Date;
  endDate: Date;
}

export const RelativeTwoPartDateObject = {
  // two-part dates
  week: '',
  'past-year-week': '',
  month: '',
  'past-year-month': '',
  quarter: '',
  'past-year-quarter': '',
};

export const RelativeDateObject = Object.assign(
  {
    today: '',
    yesterday: '',
    'current-week': '',
    'last-week': '',
    'current-month': '',
    'last-month': '',
    'current-quarter': '',
    'last-quarter': '',
    'current-year': '',
    'last-year': '',
    'past-365-days': '',
  },
  RelativeTwoPartDateObject,
);

export type RelativeDateLabels = keyof typeof RelativeDateObject;

/**
 * Service that translates dates as they appear in routes /:startDate?/:endDate?;
 * where they are absolute values in {@link dashboardApp.routeDateFormat
 * routeDateFormat}, or relative values like /today/-/.
 */
@Injectable('relativeDate')
export class RelativeDate {
  constructor(
    private dateSource: DateSource,
    private moment: (any?) => Moment,
  ) {
    'ngInject';
  }

  /**
   * Accepts string labels & convert them into dates
   * @param {IStartEndStrings} labels
   * Labels to be parse.
   * @return {IStartEndDates}
   * parsed dates
   */
  fromLabels(labels: IStartEndStrings): IStartEndDates {
    let now = this.dateSource.now();

    if (labels && angular.isObject(labels)) {
      switch (labels.startDate) {
        case 'today':
          return {
            startDate: this.moment(now).startOf('day').toDate(),
            endDate: this.moment(now).endOf('day').toDate(),
          };
        case 'yesterday':
          return {
            startDate: this.moment(now)
              .subtract(1, 'day')
              .startOf('day')
              .toDate(),
            endDate: this.moment(now).subtract(1, 'day').endOf('day').toDate(),
          };
        case 'current-week':
          return {
            startDate: this.moment(now).startOf('week').toDate(),
            endDate: this.moment(now).endOf('day').toDate(),
          };
        case 'last-week':
          return {
            startDate: this.moment(now)
              .subtract(1, 'week')
              .startOf('week')
              .toDate(),
            endDate: this.moment(now)
              .subtract(1, 'week')
              .endOf('week')
              .toDate(),
          };
        case 'current-month':
          return {
            startDate: this.moment(now).startOf('month').toDate(),
            endDate: this.moment(now).endOf('day').toDate(),
          };
        case 'last-month':
          return {
            startDate: this.moment(now)
              .subtract(1, 'month')
              .startOf('month')
              .toDate(),
            endDate: this.moment(now)
              .subtract(1, 'month')
              .endOf('month')
              .toDate(),
          };
        case 'current-quarter':
          return {
            startDate: this.moment(now).startOf('quarter').toDate(),
            endDate: this.moment(now).endOf('day').toDate(),
          };
        case 'last-quarter':
          return {
            startDate: this.moment(now)
              .subtract(1, 'quarter')
              .startOf('quarter')
              .toDate(),
            endDate: this.moment(now)
              .subtract(1, 'quarter')
              .endOf('quarter')
              .toDate(),
          };
        case 'current-year':
          return {
            startDate: this.moment(now).startOf('year').toDate(),
            endDate: this.moment(now).endOf('day').toDate(),
          };
        case 'last-year':
          return {
            startDate: this.moment(now)
              .subtract(1, 'year')
              .startOf('year')
              .toDate(),
            endDate: this.moment(now)
              .subtract(1, 'year')
              .endOf('year')
              .toDate(),
          };
        case 'past-365-days':
          return {
            startDate: this.moment(now)
              .subtract(1, 'year')
              .add(1, 'day')
              .startOf('day')
              .toDate(),
            endDate: this.moment(now).endOf('day').toDate(),
          };
        case 'week':
          return {
            startDate: this.moment(now)
              .week(parseFloat(labels.endDate))
              .startOf('week')
              .toDate(),
            endDate: this.moment(now)
              .week(parseFloat(labels.endDate))
              .endOf('week')
              .toDate(),
          };
        case 'past-year-week':
          return {
            startDate: this.moment(now)
              .subtract(1, 'year')
              .week(parseFloat(labels.endDate))
              .startOf('week')
              .toDate(),
            endDate: this.moment(now)
              .subtract(1, 'year')
              .week(parseFloat(labels.endDate))
              .endOf('week')
              .toDate(),
          };
        case 'month':
          return {
            startDate: this.moment(now)
              .month(parseFloat(labels.endDate) - 1)
              .startOf('month')
              .toDate(),
            endDate: this.moment(now)
              .month(parseFloat(labels.endDate) - 1)
              .endOf('month')
              .toDate(),
          };
        case 'past-year-month':
          return {
            startDate: this.moment(now)
              .subtract(1, 'year')
              .month(parseFloat(labels.endDate) - 1)
              .startOf('month')
              .toDate(),
            endDate: this.moment(now)
              .subtract(1, 'year')
              .month(parseFloat(labels.endDate) - 1)
              .endOf('month')
              .toDate(),
          };
        case 'quarter':
          return {
            startDate: this.moment(now)
              .quarter(parseFloat(labels.endDate))
              .startOf('quarter')
              .toDate(),
            endDate: this.moment(now)
              .quarter(parseFloat(labels.endDate))
              .endOf('quarter')
              .toDate(),
          };
        case 'past-year-quarter':
          return {
            startDate: this.moment(now)
              .subtract(1, 'year')
              .quarter(parseFloat(labels.endDate))
              .startOf('quarter')
              .toDate(),
            endDate: this.moment(now)
              .subtract(1, 'year')
              .quarter(parseFloat(labels.endDate))
              .endOf('quarter')
              .toDate(),
          };
        default:
          let startMoment: Moment, endMoment: Moment;

          if (labels.startDate && this.moment(labels.startDate).isValid()) {
            startMoment = this.moment(labels.startDate);
          } else {
            startMoment = this.moment(now);
          }

          if (labels.endDate && this.moment(labels.endDate).isValid()) {
            endMoment = this.moment(labels.endDate);
          } else {
            endMoment = this.moment(now);
          }

          return {
            startDate: startMoment.startOf('day').toDate(),
            endDate: endMoment.endOf('day').toDate(),
          };
      }
    }

    return {
      startDate: this.moment(now).startOf('day').toDate(),
      endDate: this.moment(now).endOf('day').toDate(),
    };
  }

  /**
   * Distinguish relative dates labels form all strings.
   * @param {string} value
   * value to be checked
   * @return {boolean}
   * True for labels, false otherwise
   */
  isLabel(value: string): boolean {
    return value in RelativeDateObject;
  }
}
